import { Note, NoteTag } from 'interfaces/note.interface';

export const mergeNotesPreferExisting = (
  incomingNotes: Note[],
  existingNotes: Note[]
): Note[] => {
  const existingIds = new Set(
    existingNotes.map((note) => note.id).filter(Boolean)
  );
  const uniqueIncoming = incomingNotes.filter(
    (note) => Boolean(note.id) && !existingIds.has(note.id)
  );
  return [...existingNotes, ...uniqueIncoming];
};

export const mergeNotesApiWins = (
  apiNotes: Note[],
  existingNotes: Note[]
): Note[] => {
  const apiIds = new Set(apiNotes.map((note) => note.id).filter(Boolean));
  const macOnlyNotes = existingNotes.filter(
    (note) => Boolean(note.id) && !apiIds.has(note.id)
  );
  return [...apiNotes, ...macOnlyNotes];
};

// mergeNotesApiWins keeps local notes the refetch didn't return (Mac-seeded
// ones), so a deleted note would survive the post-delete refetch without this.
export const removeNoteById = (notes: Note[], noteId: string): Note[] =>
  notes.filter((note) => note.id !== noteId);

const HTML_TAG_PATTERN = /<[^>]*>/g;
const NBSP_PATTERN = /&nbsp;/gi;

const NOTE_TAG_COLORS: string[] = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#22c55e',
  '#8b5cf6',
  '#ec4899'
];

export const getRandomNoteTagColor = (): string => {
  const index = Math.floor(Math.random() * NOTE_TAG_COLORS.length);
  return NOTE_TAG_COLORS[index];
};

export const getNotePlainText = (html: string): string =>
  html.replace(HTML_TAG_PATTERN, '').replace(NBSP_PATTERN, ' ').trim();

export const isNoteBodyEmpty = (html: string): boolean =>
  getNotePlainText(html).length === 0;

const unwrapSingleOuterParagraph = (html: string): string => {
  const match = html.match(/^<p>([\s\S]*)<\/p>$/i);
  if (!match) {
    return html;
  }

  const innerHtml = match[1];
  if (/<p[\s>]/i.test(innerHtml)) {
    return html;
  }

  return innerHtml;
};

const normalizeNoteBodyForCompare = (html: string): string => {
  if (isNoteBodyEmpty(html)) {
    return '';
  }

  return unwrapSingleOuterParagraph(
    html.replace(NBSP_PATTERN, ' ').trim()
  );
};

const getTagSignature = (tags: Array<Pick<NoteTag, 'text' | 'color'>>): string =>
  tags
    .map((tag) => `${tag.text}:${tag.color ?? ''}`)
    .sort()
    .join('|');

const hasNewNoteContent = ({
  title,
  body,
  selectedTags
}: {
  title: string;
  body: string;
  selectedTags: NoteTag[];
}): boolean =>
  title.trim().length > 0 ||
  !isNoteBodyEmpty(body) ||
  selectedTags.length > 0;

const hasEditedNoteChanges = ({
  initialNote,
  title,
  body,
  selectedTags
}: {
  initialNote: {
    title?: string;
    body?: string;
    tags?: NoteTag[];
  };
  title: string;
  body: string;
  selectedTags: NoteTag[];
}): boolean => {
  const initialTitle = (initialNote.title ?? '').trim();
  const initialBody = normalizeNoteBodyForCompare(initialNote.body ?? '');
  const initialTags = getTagSignature(initialNote.tags ?? []);

  return (
    title.trim() !== initialTitle ||
    normalizeNoteBodyForCompare(body) !== initialBody ||
    getTagSignature(selectedTags) !== initialTags
  );
};

export const hasUnsavedNoteChanges = ({
  initialNote,
  title,
  body,
  selectedTags
}: {
  initialNote: {
    title?: string;
    body?: string;
    tags?: NoteTag[];
  } | null;
  title: string;
  body: string;
  selectedTags: NoteTag[];
}): boolean => {
  if (initialNote === null) {
    return hasNewNoteContent({ title, body, selectedTags });
  }

  return hasEditedNoteChanges({
    initialNote,
    title,
    body,
    selectedTags
  });
};
